import { yupResolver } from "@hookform/resolvers/yup";
import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import useLocales from "src/hooks/useLocales";
import Context from "src/pages/App/AppSettings/Customization/context";
import palette from "src/theme/palette";
import { base64ToFile } from "src/utils/file";

import { useCreateCustomization } from "../services/useCreateCustomization";
import { useGetCustomization } from "../services/useGetCustomization";
import { customizeFormSchema, CustomizeFormValues } from "./customizeFormUtils";

export const useCustomizeForm = () => {
	const { state } = useContext(Context);
	const { translate } = useLocales();
	const { data } = useGetCustomization();

	const colorPalette = state?.type === "LIGHT" ? palette.light : palette.dark;
	const Schema = customizeFormSchema(translate);
	const hasSchema = data && data.id !== null;

	const { onSaveTheme } = useCreateCustomization();

	const defaultValues: CustomizeFormValues = {
		primaryColor: hasSchema ? data.colors.primary : colorPalette.primary.main,
		pageBackgroundColor: hasSchema ? data.colors.pageBackground : colorPalette.background.default,
		contentBackgroundColor: hasSchema
			? data.colors.contentBackground
			: colorPalette.background.paper,
		primaryTextColor: hasSchema ? data.colors.primaryText : colorPalette.text.primary,
		secondaryTextColor: hasSchema ? data.colors.secondaryText : colorPalette.text.secondary,
		strokeColor: hasSchema ? data.colors.stroke : "#919eab",
		buttonBackgroundColor: hasSchema ? data.colors.buttonBackground : colorPalette.primary.main,
		buttonTextColor: hasSchema ? data.colors.buttonText : colorPalette.common.white,
		buttonShape: hasSchema ? data.button.shape : 8,
		buttonFullWidth: hasSchema ? data.button.width : "100%",
		buttonTextWeight: hasSchema ? data.button.textWeight : "BOLD",
		mainError: hasSchema ? data.colors.mainError : colorPalette.error.main,
		lightError: hasSchema ? data.colors.lightError : colorPalette.error.lighter,
		darkError: hasSchema ? data.colors.darkError : colorPalette.error.darker,
		showExit: hasSchema ? data.showExit : true,
		file: hasSchema ? (data.logo ? base64ToFile(data?.logo, "tu_logo.png") : undefined) : undefined
	};

	const methods = useForm<CustomizeFormValues>({
		resolver: yupResolver(Schema),
		defaultValues
	});

	const onSubmit = async (data: CustomizeFormValues) => {
		onSaveTheme(data);
	};

	const reset = () => {
		methods.reset(defaultValues);
	};

	useEffect(() => {
		methods.reset(defaultValues);
	}, [data, state?.type]);

	return {
		methods,
		isLoading: false,
		onSubmit: methods.handleSubmit(onSubmit),
		reset
	};
};
